import styles from './styles.less';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import getContext from 'react-recompose/getContext';
import { defaultFormat } from '@graphistry/viz/src/formatters';
import { DataGrid } from '@graphistry/viz/src/components/data-grid';
import { Pagination } from '@graphistry/viz/src/components/pagination';
import { ColorPill } from '@graphistry/viz/src/components/color-pill';
import { ColumnPicker } from '@graphistry/viz/src/components/column-picker';
import {
    Nav,
    Row,
    Col,
    Container,
    Button,
    FormControl,
    InputGroup
} from 'react-bootstrap';

export const TabsContainerForDataTable = getContext({ socket: PropTypes.object })(function Inspector(props) {
    const { socket } = props;
    return (
        <div style={props.style} className={styles.inspector}>
            {false && socket &&
            <div className={`${styles['csv-downloader']}`}>
                <a href={`graphistry.zip?id=${socket.io.engine.id}`} download="graphistry.zip" target="_blank">
                    <i className="fa fa-download" />
                    export
                </a>
            </div> || undefined}
            <Nav
                id="inspector-tabs"
                variant="tabs"
                onSelect={props.onSelect}
                activeKey={props.openTab}>
                {(props.tabs || []).map(({ name, componentType }) => (
                    <Nav.Item key={`inspector-tab-${name}`}>
                        <Nav.Link eventKey={componentType}>{name}</Nav.Link>
                    </Nav.Item>
                ))}
            </Nav>
            {props.children}
        </div>
    );
});

const checkSearchInputValue = scanCheckSearchInputValue();

export function DataTable(props) {
    const {
        width,
        page,
        numPages,
        openTab,
        searchTerm,
        columns,
        templates,
        colsPerPage,
        rowsPerPage,
        onPage,
        onSort,
        onSearch,
        onColumnsSelect,
        selectInspectorRow,
        dataset,
        flags,
        user
    } = props;

    return (
        <Container fluid style={{ padding: 0, margin: 0 }}>
            <Row
                style={{ padding: 0, paddingTop: 4, margin: 0 }}
                className={styles['inspector-header']}>
                <Col xs={4} md={4} lg={4} style={{ paddingLeft: 0, paddingRight: 0, margin: 0 }}>
                    <InputGroup style={{ width: `100%` }}>
                            <ColumnPicker
                                value={columns}
                                dataset={dataset}
                                user={user}
                                flags={flags}
                                id="InspectorColumnPicker"
                                placeholder="Pick columns"
                                onChange={onColumnsSelect}
                                options={Array.from(templates)}
                                openTab={openTab}
                            />
                        <FormControl
                            type="text"
                            placeholder="Search"
                            defaultValue={searchTerm}
                            data-component-type={openTab}
                            ref={checkSearchInputValue(searchTerm)}
                            onChange={e => onSearch(e.target.value)}
                        />
                    </InputGroup>
                </Col>
                <Col xs={8} md={8} lg={8} style={{ paddingTop: 0, margin: 0 }}>
                    <Pagination
                        ellipsis
                        next
                        prev
                        page={page}
                        pages={numPages}
                        onSelect={onPage}
                        maxButtons={Math.floor(width / 75)}
                        btnClass={`${styles['paginator-btn']} w-100`}
                    />
                </Col>
            </Row>
            <Row style={{ padding: 0, margin: 0, height: `100%` }}>
                <Col xs={12} md={12} lg={12} style={{ padding: 0, margin: 0 }}>
                    <DataGrid
                        {...props}
                        renderCell={renderCell}
                        onRowSelect={onRowSelect}
                        onColHeaderSelect={onColHeaderSelect}
                        renderColHeaderCell={renderColHeaderCell}
                        renderRowHeaderCell={renderRowHeaderCell}
                    />
                </Col>
            </Row>
        </Container>
    );

    function onRowSelect({ currentTarget: target }) {
        const { rows, startRow } = props;
        const { rowIndex } = target.dataset;
        const row = rows[rowIndex - startRow];
        if (row && typeof row._index === 'number') {
            selectInspectorRow({
                index: row._index,
                componentType: openTab === 'event' ? 'point' : openTab
            });
        }
    }

    function onColHeaderSelect({ currentTarget: target }) {
        const { cols } = props;
        const { colIndex } = target.dataset;
        const col = cols[colIndex];
        if (col && col.name) {
            onSort(col.name);
        }
    }
}

function scanCheckSearchInputValue(componentType) {
    return function checkSearchInputValue(searchTerm) {
        return function checkSearchInputValueRef(ref) {
            if (
                ref &&
                ref.value !== searchTerm &&
                ref.props &&
                componentType !== (componentType = ref.props['data-component-type'])
            ) {
                findDOMNode(ref).value = searchTerm;
            }
        };
    };
}

function renderRowHeaderCell(rowIndex, { rows, startRow }, isTopLeftCell) {
    const show = !isTopLeftCell && rows[rowIndex - startRow];
    return (
        <span className={show ? '' : styles['head-row-cell-content-hidden']}>
            {rowIndex + 1 /* force header to measure width of last rowIndex */}
        </span>
    );
}

function renderColHeaderCell(colIndex, { cols, sortKey, sortOrder, entityType }) {
    const col = cols[colIndex];

    if (!col) {
        return '';
    }

    const { name = '\u00a0' } = col;
    const isSorting = sortKey === name;
    const label = name === '_title' ? entityType : name;

    return (
        <Button title={label} block active={isSorting} className={styles['inspector-header-cell']}>
            <i
                className={classNames({
                    fa: true,
                    'fa-fw': true,
                    'fa-sort': !isSorting,
                    [styles['sort-active']]: isSorting,
                    [`fa-sort-${sortOrder}`]: isSorting,
                    [styles['sort-inactive']]: !isSorting
                })}
            />
            <span>{label}</span>
        </Button>
    );
}

function renderLoadingCell() {
    return (
        <p className={styles['loading-cell']}>
            <i className="fa fa-ellipsis-h" />
        </p>
    );
}

function renderCell(colIndex, rowIndex, { loading, cols, rows, startCol, startRow, loadingRow }) {
    const col = cols[colIndex];
    const row = rows[rowIndex - startRow];
    let dataType, value = undefined, __html = '';

    if (row && col) {
        if (row === loadingRow) {
            return renderLoadingCell();
        }

        value = row[col.name];

        if (value === undefined && row.$__status === 'pending') {
            return (
                <p className={styles['loading-cell']}>
                    <i className="fa fa-ellipsis-h" />
                </p>
            );
        }

        if (col.dataType === 'date') {
            dataType = 'date';
        } else if (col.dataType === 'bool') {
            dataType = 'bool';
        } else if (value != null && value !== '') {
            value = '' + value;
            dataType = col.dataType;
        }
    }

    if (value && dataType === 'color') {
        return (
            <span title={value}>
                <ColorPill color={value} /> {value}
            </span>
        );
    } else if (value && dataType === 'date') {
        value = __html = defaultFormat(value, dataType);
    } else {
        __html = defaultFormat(value, dataType);
        if (__html === null) {
            if (loading) {
                return renderLoadingCell();
            } else {
                __html = '';
            }
        }
    }
    
    return <span title={value} dangerouslySetInnerHTML={{ __html }} />;
}